import React, { useRef, useEffect, useState, useMemo } from 'react';
import * as d3 from 'd3';
import { AlertTriangle } from 'lucide-react';

/**
 * TextDiversityHeatmap
 * Same grid language as the Outliers / Completeness / Duration heatmaps:
 *   columns = data collectors, rows = open-ended variables,
 *   cell    = share of that collector's answers to that variable that are unique.
 * High diversity (varied answers) = green; low diversity (templated / copy-paste) = red.
 *
 * Works with whatever the backend gives:
 *   - per-variable diversity per collector (collector.per_variable / by_variable / variables)
 *     -> full collectors x variables grid
 *   - only an overall_diversity per collector -> a single "Overall" row
 *
 * Props
 *   collectors   byCollector.collectors  ({ id: { collector_id, total_responses, overall_diversity, diversity_measured, per_variable? } })
 *   variables    optional ordered list of variable names to show as rows
 *   threshold    diversity below this (0..1) counts as low (default 0.5)
 *   isDarkMode
 */

const pickRatio = (v) => {
  if (v === null || v === undefined) return null;
  if (typeof v === 'number') return v > 1 ? v / 100 : v;
  const keys = ['unique_response_ratio', 'diversity', 'overall_diversity', 'unique_ratio', 'diversity_score', 'ratio'];
  for (const k of keys) if (typeof v[k] === 'number') return v[k] > 1 ? v[k] / 100 : v[k];
  return null;
};
const pickCount = (v) => {
  if (!v || typeof v !== 'object') return null;
  const keys = ['valid_responses', 'total_responses', 'responses', 'count', 'n'];
  for (const k of keys) if (typeof v[k] === 'number') return v[k];
  return null;
};

const TextDiversityHeatmap = ({ collectors = {}, variables = null, threshold = 0.5, isDarkMode = false, height = null }) => {
  const ref = useRef();
  const containerRef = useRef();
  const [width, setWidth] = useState(800);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const update = () => { if (el.offsetWidth > 0) setWidth(el.offsetWidth); };
    update();
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver((entries) => {
        const w = entries[0]?.contentRect?.width;
        if (w > 0) setWidth(w);
      });
      ro.observe(el);
    }
    const onResize = () => requestAnimationFrame(update);
    window.addEventListener('resize', onResize);
    const t = setTimeout(update, 100);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', onResize);
      clearTimeout(t);
    };
  }, []);

  const model = useMemo(() => {
    const list = Object.values(collectors || {}).filter((c) => c && (c.collector_id !== undefined));
    if (!list.length) return null;
    const ids = list.map((c) => String(c.collector_id)).sort();
    const byId = {};
    list.forEach((c) => { byId[String(c.collector_id)] = c; });

    // Discover per-variable data
    const perVarKey = ['per_variable', 'by_variable', 'variables'].find((k) => list.some((c) => c[k] && typeof c[k] === 'object'));
    let rows = [];
    const cells = [];
    if (perVarKey) {
      const seen = new Set();
      list.forEach((c) => Object.keys(c[perVarKey] || {}).forEach((v) => seen.add(v)));
      rows = variables ? variables.filter((v) => seen.has(v)) : Array.from(seen).sort();
      ids.forEach((id) => {
        const pv = byId[id][perVarKey] || {};
        rows.forEach((v) => {
          const r = pickRatio(pv[v]);
          if (r !== null) cells.push({ collector: id, row: v, value: r, n: pickCount(pv[v]) });
        });
      });
    }
    const mode = rows.length && cells.length ? 'variables' : 'overall';
    if (mode === 'overall') {
      rows = ['Overall'];
      ids.forEach((id) => {
        const c = byId[id];
        const measured = c.diversity_measured ?? true;
        const r = pickRatio(c.overall_diversity);
        if (measured && r !== null) cells.push({ collector: id, row: 'Overall', value: r, n: c.total_responses ?? null });
      });
    }
    return { ids, rows, cells, mode };
  }, [collectors, variables]);

  // Colour: 0 = red (templated) ... threshold = amber ... 1 = green (varied)
  const colorScale = useMemo(() => d3.scaleLinear()
    .domain([0, Math.max(0.01, threshold * 0.6), threshold, Math.min(1, threshold + 0.25), 1])
    .range(['#EF4444', '#FB923C', '#F59E0B', '#c3d334', '#10B981'])
    .interpolate(d3.interpolateRgb.gamma(2.2)), [threshold]);

  useEffect(() => {
    if (!model || !ref.current) return undefined;
    const svg = d3.select(ref.current);
    svg.selectAll('*').remove();

    const { ids, rows, cells } = model;
    const rowLabel = (r) => (r.length > 22 ? `${r.slice(0, 20)}\u2026` : r);
    const margin = { top: 20, right: 140, bottom: 130, left: 150 };
    const w = Math.max(width, 320);
    const availableWidth = w - margin.left - margin.right - 20;
    const cellW = Math.max(30, Math.min(60, availableWidth / ids.length));
    const cellH = rows.length === 1 ? 52 : Math.max(34, Math.min(60, cellW));

    const innerWidth = cellW * ids.length;
    const innerHeight = cellH * rows.length;
    const totalWidth = innerWidth + margin.left + margin.right;
    const totalHeight = innerHeight + margin.top + margin.bottom;

    const x = d3.scaleBand().domain(ids).range([0, innerWidth]).padding(0.08);
    const y = d3.scaleBand().domain(rows).range([0, innerHeight]).padding(0.1);
    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

    const muted = isDarkMode ? '#94A3B8' : '#64748B';
    const strong = isDarkMode ? '#E2E8F0' : '#1E293B';

    const tooltip = d3.select('body').append('div')
      .attr('class', 'text-diversity-heatmap-tooltip')
      .style('position', 'absolute').style('visibility', 'hidden')
      .style('background', isDarkMode
        ? 'linear-gradient(135deg, rgba(23, 23, 27, 0.98) 0%, rgba(13, 14, 14, 0.98) 100%)'
        : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(249, 250, 251, 0.98) 100%)')
      .style('color', isDarkMode ? '#E5E7EB' : '#0F172A')
      .style('padding', '14px 18px')
      .style('border', isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)')
      .style('border-radius', '12px')
      .style('box-shadow', isDarkMode ? '0 12px 40px rgba(0, 0, 0, 0.4)' : '0 12px 40px rgba(0, 0, 0, 0.15)')
      .style('font-size', '13px')
      .style('font-family', "'Outfit', -apple-system, sans-serif")
      .style('pointer-events', 'none').style('z-index', '1000').style('backdrop-filter', 'blur(20px)');

    // Empty cells (collector had no measurable text for this variable)
    const filled = new Set(cells.map((c) => `${c.collector}|${c.row}`));
    const empties = [];
    ids.forEach((c) => rows.forEach((r) => { if (!filled.has(`${c}|${r}`)) empties.push({ collector: c, row: r }); }));
    g.selectAll('rect.empty').data(empties).enter().append('rect')
      .attr('x', (d) => x(d.collector)).attr('y', (d) => y(d.row))
      .attr('width', x.bandwidth()).attr('height', y.bandwidth())
      .attr('rx', 6).attr('ry', 6)
      .attr('fill', isDarkMode ? 'rgba(255,255,255,0.035)' : 'rgba(0,0,0,0.035)');

    g.selectAll('rect.cell').data(cells).enter().append('rect')
      .attr('class', 'cell')
      .attr('x', (d) => x(d.collector)).attr('y', (d) => y(d.row))
      .attr('width', x.bandwidth()).attr('height', y.bandwidth())
      .attr('fill', (d) => colorScale(d.value))
      .attr('rx', 6).attr('ry', 6)
      .style('cursor', 'pointer')
      .style('filter', (d) => (d.value < threshold ? `drop-shadow(0 2px 8px ${colorScale(d.value)}40)` : 'none'))
      .style('transform-origin', 'center').style('transform-box', 'fill-box')
      .on('mouseover', function (event, d) {
        const c = colorScale(d.value);
        d3.select(this).transition().duration(200).attr('rx', 8).attr('ry', 8)
          .style('transform', 'scale(1.12)').style('filter', `drop-shadow(0 4px 12px ${c}60)`);
        const line = (k, v, col) => `<div style="display:flex;justify-content:space-between;gap:18px;margin-bottom:6px;"><span style="color:${muted};">${k}</span><span style="font-weight:600;${col ? `color:${col};` : ''}">${v}</span></div>`;
        tooltip.style('visibility', 'visible').html(`
          <div style="margin-bottom:10px;font-weight:600;font-size:13.5px;border-bottom:1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'};padding-bottom:8px;">${d.collector}${d.row !== 'Overall' ? ` &middot; ${d.row}` : ''}</div>
          ${line('Unique responses', `${(d.value * 100).toFixed(0)}%`, c)}
          ${d.n !== null && d.n !== undefined ? line('Responses', d.n.toLocaleString()) : ''}
          <div style="margin-top:6px;font-size:11.5px;color:${c};font-weight:600;">${d.value < threshold ? 'Low diversity: answers look repetitive' : 'Healthy variety of answers'}</div>
        `);
      })
      .on('mousemove', function (event) { tooltip.style('top', `${event.pageY - 10}px`).style('left', `${event.pageX + 12}px`); })
      .on('mouseout', function (event, d) {
        d3.select(this).transition().duration(200).attr('rx', 6).attr('ry', 6)
          .style('transform', 'scale(1)')
          .style('filter', d.value < threshold ? `drop-shadow(0 2px 8px ${colorScale(d.value)}40)` : 'none');
        tooltip.style('visibility', 'hidden');
      });

    const fontSize = Math.max(9.5, Math.min(12, cellW / 5));
    g.selectAll('text.cell-value').data(cells).enter().append('text')
      .attr('x', (d) => x(d.collector) + x.bandwidth() / 2)
      .attr('y', (d) => y(d.row) + y.bandwidth() / 2)
      .attr('text-anchor', 'middle').attr('dominant-baseline', 'middle')
      .style('font-size', `${fontSize}px`).style('font-weight', '700').style('letter-spacing', '-0.01em')
      .style('font-family', "'Outfit', -apple-system, sans-serif")
      .style('fill', '#17171B').style('pointer-events', 'none')
      .text((d) => `${Math.round(d.value * 100)}%`);

    const xAxis = g.append('g').attr('transform', `translate(0,${innerHeight})`).call(d3.axisBottom(x));
    xAxis.selectAll('text').style('fill', muted).style('font-size', '12px').style('font-weight', '500')
      .style('text-anchor', 'end').attr('dx', '-0.5em').attr('dy', '0em').attr('transform', 'rotate(-90)');
    xAxis.selectAll('path, line').style('stroke', 'transparent');

    const yAxis = g.append('g').call(d3.axisLeft(y).tickFormat(rowLabel));
    yAxis.selectAll('text').style('fill', muted).style('font-size', '12px').style('font-weight', '500');
    yAxis.selectAll('text').append('title').text((d) => d);
    yAxis.selectAll('path, line').style('stroke', 'transparent');

    g.append('text').attr('x', innerWidth / 2).attr('y', innerHeight + margin.bottom - 20)
      .attr('text-anchor', 'middle').style('font-size', '14px').style('font-weight', '700').style('fill', strong)
      .style('letter-spacing', '0.02em').text('Data Collectors');
    g.append('text').attr('transform', 'rotate(-90)').attr('x', -innerHeight / 2).attr('y', -margin.left + 25)
      .attr('text-anchor', 'middle').style('font-size', '14px').style('font-weight', '700').style('fill', strong)
      .style('letter-spacing', '0.02em').text(model.mode === 'variables' ? 'Open-ended Variables' : 'Response Diversity');

    // Legend
    const legendWidth = 24;
    const legendHeight = Math.min(Math.max(innerHeight * 0.6, 120), 180);
    const legendX = totalWidth - margin.right + 30;
    const legendY = margin.top + Math.max(0, (innerHeight - legendHeight) / 2);
    const defs = svg.append('defs');
    const gradientId = `text-diversity-gradient-${Math.random().toString(36).slice(2, 11)}`;
    const gradient = defs.append('linearGradient').attr('id', gradientId)
      .attr('x1', '0%').attr('x2', '0%').attr('y1', '100%').attr('y2', '0%');
    gradient.selectAll('stop')
      .data([
        { offset: '0%', color: '#EF4444' },
        { offset: `${threshold * 60}%`, color: '#FB923C' },
        { offset: `${threshold * 100}%`, color: '#F59E0B' },
        { offset: `${Math.min(100, (threshold + 0.25) * 100)}%`, color: '#c3d334' },
        { offset: '100%', color: '#10B981' },
      ])
      .enter().append('stop').attr('offset', (d) => d.offset).attr('stop-color', (d) => d.color);
    const shadowId = `legend-shadow-${Math.random().toString(36).slice(2, 11)}`;
    defs.append('filter').attr('id', shadowId).append('feDropShadow')
      .attr('dx', 0).attr('dy', 2).attr('stdDeviation', 4).attr('flood-opacity', 0.15);
    const legend = svg.append('g');
    legend.append('rect').attr('x', legendX).attr('y', legendY).attr('width', legendWidth).attr('height', legendHeight)
      .attr('rx', 6).attr('ry', 6).style('fill', `url(#${gradientId})`).style('filter', `url(#${shadowId})`);
    [
      { y: legendY, text: '100% unique' },
      { y: legendY + legendHeight * (1 - threshold), text: `${Math.round(threshold * 100)}% (review)` },
      { y: legendY + legendHeight, text: '0% unique' },
    ].forEach((l) => {
      legend.append('text').attr('x', legendX + legendWidth + 10).attr('y', l.y)
        .style('text-anchor', 'start').style('dominant-baseline', 'middle')
        .style('font-size', '11.5px').style('font-weight', '600').style('fill', muted).text(l.text);
    });
    legend.append('text').attr('x', legendX + legendWidth / 2).attr('y', legendY - 14)
      .style('text-anchor', 'middle').style('font-size', '12px').style('font-weight', '700').style('fill', strong)
      .style('letter-spacing', '0.02em').text('Diversity');

    svg.attr('width', totalWidth).attr('height', totalHeight);
    return () => { tooltip.remove(); };
  }, [model, width, isDarkMode, threshold, colorScale]);

  if (!model || model.cells.length === 0) {
    return (
      <div style={{
        width: '100%', height: height || 260, display: 'flex', alignItems: 'center', justifyContent: 'center',
        borderRadius: '12px', border: isDarkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)',
        color: isDarkMode ? '#94A3B8' : '#64748B', fontSize: '14px', fontWeight: 500,
      }}>
        <div style={{ textAlign: 'center' }}>
          <AlertTriangle size={32} style={{ marginBottom: '12px', opacity: 0.5 }} />
          <div>No measurable text responses per collector</div>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-scroller" ref={containerRef}
      style={{ width: '100%', maxWidth: '100%', overflow: 'auto', position: 'relative' }}>
      <svg ref={ref} style={{ borderRadius: '12px', display: 'block', minWidth: '100%' }} />
    </div>
  );
};

export default TextDiversityHeatmap;